import React from "react";
import generateCorrespondentVariant from "utils/option-variant";
import Image from "components/image";
import Link from "components/link";
import clsx from "clsx/lite";
import CustomDropdown from "components/dropdown/custom";
import { ReactComponent as InfoIcon } from "assets/svgs/info.svg";
import OptionsInfoModal from "./options-info-modal";
import useToggle from "hooks/useToggle";

export const DropdownOption = ({ option, variant, variants, setSelectedVariant, handle, options, currentValue, renderAsLink = true }) => {
	const data = option.values?.find((data) => data.value === currentValue);
	const current = currentValue || variant?.store[`option${+option._key + 1}`];

	const handleSelect = (value) => {
		const correspondentVariant = generateCorrespondentVariant(options, option, value, variants, variant);
		setSelectedVariant(correspondentVariant);
	};

	const Label = () => (
		<p className="text-base pb-2">
			{option.name}: <span className="font-semibold pl-1">{current}</span>
		</p>
	);

	const Preview = () => (
		<>
			{renderAsLink && <IconColor data={data} />}
			<span className="line-clamp-1 text-start">{current}</span>
		</>
	);


	return (
		<CustomDropdown
			Label={() => !renderAsLink && <Label />}
			Preview={() => <Preview />}
			values={option.values}
			onSelect={handleSelect}
			buttonClassName="bg-white pr-3"
		>
			{option?.values.map(({ value, icon, color, type }, index) => {
				const correspondentVariant =
					generateCorrespondentVariant(options, option, value, variants, variant);
				return renderAsLink ? (
					<Link
						key={index}
						aria-label={option.name + value}
						replace
						state={{ preserveScroll: true, variantChanged: !(variant.handle === variants[0].handle) }}
						to={variants[0].handle === correspondentVariant?.handle ?
							`/products/${handle}` :
							`/products/${handle}/${correspondentVariant?.handle}`}
						className={clsx(
							"pl-3 md:pl-4 py-2.5 text-darkGray flex gap-3 items-center",
							value === current
								? "bg-seashell hover:bg-seashell hover:text-darkGray"
								: "hover:bg-cream hover:text-black"
						)}
					>
						<IconColor data={{ icon, color, type }} />
						<span>{value}</span>
					</Link>
				) : (
					<button
						key={index}
						value={value}
						className={clsx(
							"pl-4 p-2.5 hover:bg-cream text-darkGray flex gap-3 items-center w-full",
							value === current && "bg-seashell hover:bg-seashell hover:text-darkGray"
						)}
					>
						{renderAsLink && <IconColor data={{ icon, color, type }} />}
						<span>{value}</span>
					</button>
				);
			})}
		</CustomDropdown>
	);
};

export const IconColor = ({ data }) => {
	switch (data?.type) {
		case "color":
			return <div className="size-5 rounded-full" style={{ backgroundColor: data?.color?.hex }} />;
		case "icon":
			return <Image className="size-4" image={data?.icon} />;
		default:
			return null;
	}
};

const ButtonsOption = ({ path, data, isActive, state, singleValue }) => (
	<Link to={path} state={state} replace
		className={clsx(
			"button-option ",
			isActive ? "bg-seashell border border-sunray" : "bg-white",
			(data?.icon || data?.color) && "pr-8 flex-1 md:flex-initial",
			singleValue && "w-full sm:w-auto")}
	>
		<IconColor data={data} />
		{data?.value}
	</Link>
);

const ColorsOption = ({ data, path, isActive, state }) => (
	<Link
		to={path}
		activeStyle={{ borderColor: isActive ? data?.color?.hex : "" }}
		state={state}
		replace
		className="size-10 rounded-full border-2 border-white flex items-center justify-center">
		<div style={{ backgroundColor: data?.color?.hex }} className="size-8 rounded-full"></div>
	</Link>
);

const IconsOption = ({ path, data, isActive, state }) => (
	<Link
		to={path}
		state={state}
		replace
		className={clsx("border bg-white", isActive && "border-black")}>
		{data?.icon && <Image width={50} image={data.icon} />}
	</Link>
);

const optionType = {
	buttons: ButtonsOption,
	colors: ColorsOption,
	icons: IconsOption,
};

const ProductOptions = ({ options, variants, variant, handle }) => {
	if (options[0].values[0].value === "Default Title") return null;

	return (
		<div className="flex flex-col sm:flex-row flex-wrap w-fit sm:w-auto gap-x-3 sm:gap-x-5 gap-y-5">
			{options.map((option) => {
				const currentValue = variant.store[`option${+option._key + 1}`];
				const OptionComponent = optionType?.[option?.template];
				const isDropdown = option.template === "dropdown";
				const singleValue = option.values.length === 1;
				const [isModalOpen, toggleModal] = useToggle();

				return (
					<div
						key={option._key}
						className={clsx(
							isDropdown && options.length > 1 ?
								"xl:flex-1 min-w-fit w-full sm:w-auto" : "sm:w-auto w-fit",
							option.template === "buttons" && (singleValue ? "flex-auto sm:flex-none" : "max-w-fit")
						)}
					>
						{isModalOpen && <OptionsInfoModal data={option.infoModal} isOpen={isModalOpen} toggle={toggleModal} />}
						<p className="mb-2">
							<span className="text-secondary flex items-center">
								{option.name}:
								{["icons", "colors"].includes(option.template) && <span className="ml-1"> {currentValue}</span>}
								{option.infoModal && (
									<button onClick={toggleModal} className="mx-1.5 mt-0.5 t-option-info-trigger">
										<InfoIcon />
									</button>
								)}
							</span>
						</p>
						{isDropdown ? <DropdownOption
							option={option} currentValue={currentValue} variants={variants}
							variant={variant} handle={handle} options={options}
						/> :
							<div className="flex gap-5 flex-wrap">
								{option.values.map((data, index) => {
									const correspondentVariant =
										generateCorrespondentVariant(options, option, data.value, variants, variant);
									const path =
										`/products/${handle}${variants[0].handle === correspondentVariant?.handle ?
											"" :
											`/${correspondentVariant?.handle}`}`;
									return (
										<OptionComponent
											key={index}
											isActive={currentValue === data.value}
											path={path}
											data={data}
											singleValue={singleValue}
											state={{
												preserveScroll: true,
												variantChanged: variant.handle !== variants[0].handle
											}}
										/>
									);
								})}
							</div>
						}
					</div>
				);
			})}
		</div>
	);
};

export default ProductOptions;